// The field capture screens' rules, pure so they are tested here: which project is nearest,
// how a distance and a waiting count read, what a phone calls itself, and how many photos a
// checklist item has once this phone's queue is counted in.

import type { QueuedUpload } from "@/lib/upload-queue";

interface Point {
  lat: number;
  lng: number;
}

const EARTH_RADIUS_KM = 6371;
const rad = (deg: number) => (deg * Math.PI) / 180;

/** Great-circle distance in kilometres. */
export function distanceKm(a: Point, b: Point): number {
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

interface Placed {
  name: string;
  lat: number | null;
  lng: number | null;
}

/**
 * Projects with their distance from the phone, nearest first. Without a position, or for a
 * project with no centroid, the distance is null; those come after, by name.
 */
export function byDistance<P extends Placed>(
  projects: readonly P[],
  position: Point | null,
): { project: P; km: number | null }[] {
  return projects
    .map((project) => ({
      project,
      km:
        position && project.lat !== null && project.lng !== null
          ? distanceKm(position, { lat: project.lat, lng: project.lng })
          : null,
    }))
    .sort((a, b) => {
      if (a.km !== null && b.km !== null && a.km !== b.km) return a.km - b.km;
      if (a.km !== null && b.km === null) return -1;
      if (a.km === null && b.km !== null) return 1;
      return a.project.name.localeCompare(b.project.name);
    });
}

/** "0.3 km away", "1.2 km away", "86 km away". */
export function distanceLabel(km: number): string {
  const text = km < 10 ? km.toFixed(1) : Math.round(km).toLocaleString("en-US");
  return `${text} km away`;
}

const plural = (n: number, one: string, many: string) =>
  `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;

export function waitingLabel(count: number): string {
  return `${plural(count, "photo", "photos")} waiting to upload`;
}

/** "8 photos have not uploaded since 9:14 AM. Open the app on Wi-Fi or signal before you leave site." */
export function staleLabel(count: number, since: number): string {
  const time = new Date(since).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  const verb = count === 1 ? "has" : "have";
  return `${plural(count, "photo", "photos")} ${verb} not uploaded since ${time}. Open the app on Wi-Fi or signal before you leave site.`;
}

export function albumCountLabel(count: number): string {
  return plural(count, "album", "albums");
}

export function photoCountLabel(count: number): string {
  return plural(count, "photo", "photos");
}

interface NavigatorLike {
  userAgent: string;
  userAgentData?: { platform?: string };
}

/** What the photo says took it: the platform Chromium reports, or a short name from the UA. */
export function deviceName(nav: NavigatorLike): string | null {
  const platform = nav.userAgentData?.platform?.trim();
  if (platform) return platform;
  const ua = nav.userAgent;
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android";
  if (/Macintosh/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows";
  return null;
}

/** iPadOS reports itself as a Mac; a touch screen gives it away. */
export function isIos(nav: NavigatorLike & { maxTouchPoints?: number }): boolean {
  return (
    /iPhone|iPad|iPod/.test(nav.userAgent) ||
    (/Macintosh/.test(nav.userAgent) && (nav.maxTouchPoints ?? 0) > 1)
  );
}

/** "IMG_20261104_091422.jpg" from a "YYYY-MM-DDTHH:mm:ss" wall clock. */
export function cameraFileName(capturedAt: string): string {
  return `IMG_${capturedAt.replace(/-/g, "").replace("T", "_").replace(/:/g, "")}.jpg`;
}

type QueuedForCount = Pick<QueuedUpload, "albumId" | "status" | "sentAt">;

/**
 * This phone's photos for an album that the server's counts do not have yet: everything not
 * sent, and anything sent after the server's count was read (`countedAt`, epoch ms).
 */
export function pendingForAlbum<Q extends QueuedForCount>(
  queue: readonly Q[],
  albumId: string,
  countedAt: number,
): Q[] {
  return queue.filter(
    (q) =>
      q.albumId === albumId && (q.status !== "sent" || (q.sentAt !== null && q.sentAt > countedAt)),
  );
}

/** Those photos grouped by checklist item; photos for the album as a whole are left out. */
export function byItem<Q extends { itemId: string | null }>(
  pending: readonly Q[],
): Map<string, Q[]> {
  const groups = new Map<string, Q[]>();
  for (const q of pending) {
    if (!q.itemId) continue;
    const list = groups.get(q.itemId) ?? [];
    list.push(q);
    groups.set(q.itemId, list);
  }
  return groups;
}

interface Shot {
  id: string;
  requiredCount: number;
}

/** The first item, in checklist order, with fewer photos than it needs. */
export function nextMissing<S extends Shot>(
  items: readonly S[],
  have: (item: S) => number,
): S | null {
  return items.find((item) => have(item) < item.requiredCount) ?? null;
}

/** Items that have every photo they need, for "3 of 7 shots". */
export function shotsDone(items: readonly (Shot & { assignedCount: number })[]): number {
  return items.filter((item) => item.assignedCount >= item.requiredCount).length;
}
